import React, { useEffect, useState, useCallback } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import profileDefault from "../../assets/images/profileDefault.png";

// Profile-view log: who viewed which user's profile, and from where
// (Discover row, etc). Backed by the admin-only GET /users/admin/profile-views
// endpoint, itself reading UserEngagement event_type 'user_profile_viewed'.
// Mirrors the DiscoverInvites page pattern (self-contained dark table).
const ProfileViews = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    total_views: 0,
    unique_viewers: 0,
    unique_viewed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchViews = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/users/admin/profile-views`);
      setRows(data?.data || []);
      setSummary({
        total_views: data?.total_views || 0,
        unique_viewers: data?.unique_viewers || 0,
        unique_viewed: data?.unique_viewed || 0,
      });
    } catch (err) {
      console.error("Failed to fetch profile views:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  const filtered = rows.filter((r) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (r.viewer.name || "").toLowerCase().includes(s) ||
      (r.viewer.email || "").toLowerCase().includes(s) ||
      (r.viewed.name || "").toLowerCase().includes(s) ||
      (r.viewed.email || "").toLowerCase().includes(s)
    );
  });

  const personCell = (p) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <img
        src={p.photo_url || profileDefault}
        alt=""
        style={{ width: 32, height: 32, borderRadius: 16, objectFit: "cover", background: "#222" }}
      />
      <div>
        <div>
          {p.name || "—"}
          {p.internal && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: "rgba(255,255,255,0.5)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 4,
                padding: "1px 5px",
                textTransform: "uppercase",
              }}
            >
              internal
            </span>
          )}
        </div>
        {p.email && (
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{p.email}</div>
        )}
      </div>
    </div>
  );

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ width: "100%", marginBottom: 16 }}>
            <h1 style={{ margin: 0, color: "#fff" }}>Profile Views</h1>
            <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
              Who viewed which user's profile, and from where. Rows tagged
              "internal" are team/test accounts, not real user behavior.
            </p>
          </div>

          <div style={{ width: "100%", display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            {[
              { num: summary.total_views, label: "Total views" },
              { num: summary.unique_viewers, label: "Unique viewers" },
              { num: summary.unique_viewed, label: "Unique people viewed" },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 12,
                  padding: "16px 20px",
                  minWidth: 170,
                }}
              >
                <div style={{ fontSize: 28, fontWeight: 800, color: "#4F30EA", lineHeight: 1.1 }}>
                  {s.num}
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div style={{ width: "100%", marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search viewer or viewed name/email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 13,
                outline: "none",
                width: 300,
              }}
            />
          </div>

          <div
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 720 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={th}>Viewer</th>
                  <th style={th}>Viewed</th>
                  <th style={th}>Source</th>
                  <th style={th}>When</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      No profile views recorded yet.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={td}>{personCell(r.viewer)}</td>
                      <td style={td}>{personCell(r.viewed)}</td>
                      <td style={td}>{r.source || "—"}</td>
                      <td style={td}>{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </main>
  );
};

const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
};
const td = {
  padding: "12px 14px",
  fontSize: 13,
  verticalAlign: "middle",
};

export default ProfileViews;
